import Button from "@mui/material/Button";
import Grid2 from "@mui/material/Grid2";
import { AgGridReact } from "ag-grid-react";
import type {
	ColDef,
	DefaultMenuItem,
	EditableCallbackParams,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
} from "ag-grid-community";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormsContext } from "../../context/FormsContext";
import { LazyLoading } from "@/components/loaders/Loader/LazyLoading";
import { ErrorModal } from "@dexteel/mesf-core";
import FormatListBulletedSharpIcon from "@mui/icons-material/FormatListBulletedSharp";
import { useSearchUsersFormAsset } from "../../hooks/useSearchUsersFormAsset";
import type { UserFormAssetData } from "../../models/userFormAssetData";
import { DeleteUserFormAssetDialog } from "./components/delete-user-form-asset-modal/delete-user-form-asset-modal";
import { NewAndEditUserFormAssetModal } from "./components/new-user-form-asset-modal/modal-new-and-edit-user-form-asset";
import { INITIAL_VALUES_USER_FORM_ASSET } from "../../models/UserFormAssetInitialValue";
import { EditFormAsset } from "./components/edit-form-asset-modal/edit-form-asset-modal";
import { useSearchFormAsset } from "../../hooks/useSearchFormAsset";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";

type Props = {
	FormAssetIdToSearchUserFormAssets: number;
	setOpenNewEditUserFormAsset: (setter: boolean) => void;
	setOpenDeleteUserFormAsset: (setter: boolean) => void;
	setNewUserFormAssetMode: (setter: boolean) => void;
	setShouldUpdateWhenExit: (shouldUpdate: boolean) => void;
	openNewEditUserFormAsset: boolean;
	openDeleteUserFormAsset: boolean;
	newUserFormAssetMode: boolean;
};

export const UsersFormAssetTable = ({
	setShouldUpdateWhenExit,
	FormAssetIdToSearchUserFormAssets,
	setOpenNewEditUserFormAsset,
	openNewEditUserFormAsset,
	openDeleteUserFormAsset,
	setOpenDeleteUserFormAsset,
	newUserFormAssetMode,
	setNewUserFormAssetMode,
}: Props) => {
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const gridStyle = useMemo(
		() => ({ height: "100%", width: "100%", padding: 10 }),
		[],
	);
	const [UsersFormAssetColumnDefs, setUsersFormAssetColumnDefs] = useState<
		ColDef[]
	>([]);

	const {
		state: {
			userFormAssetDataSelectedInTable,
			usersFormAssetList,
			FormAsset: { FormAssetId },
			FormAsset,
		},
		actions: {
			setUserFormAssetDataSelectedInTable,
			setUserIdFormAssetDataSelectedInTable,
			setIsDefaultUserFormAssetDataSelectedInTable,
		},
	} = useFormsContext();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [error, setError] = useState<string>("");
	const searchUsersFormAsset = useSearchUsersFormAsset({
		setIsLoading,
		setError,
		FormAssetIdToSearchUserFormAssets,
	});
	const searchFormAsset = useSearchFormAsset({
		setIsLoading,
		setError,
		FormAssetIdToSearchUserFormAssets,
	});

	const loadingOverlayComponent = useMemo<any>(() => {
		return LazyLoading;
	}, []);

	const onActionsClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		params: EditableCallbackParams,
	) => {
		(params.api as any).showContextMenu({
			column: params.column,
			rowNode: params.node,
			undefined,
			mouseEvent: e.nativeEvent,
		});
	};

	const getUsersFormAssetContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data: UserFormAssetData = params.node?.data;
			const result: (DefaultMenuItem | MenuItemDef)[] = [];
			if (data) {
				result.push({
					name: "Edit User",
					action: () => {
						setNewUserFormAssetMode(false);
						setUserFormAssetDataSelectedInTable(data);
						setUserIdFormAssetDataSelectedInTable(data.UserId);
						setIsDefaultUserFormAssetDataSelectedInTable(data.IsDefault);
						setOpenNewEditUserFormAsset(true);
					},
					icon: '<i class="fas fa-edit"></i>',
				});
			}

			if (data) {
				result.push({
					name: "Add User",
					action: () => {
						setUserFormAssetDataSelectedInTable(INITIAL_VALUES_USER_FORM_ASSET);
						setNewUserFormAssetMode(true);
						setOpenNewEditUserFormAsset(true);
					},
					icon: '<i class="fas fa-plus"></i>',
				});
			}

			if (data) {
				result.push({
					name: "Delete User",
					action: () => {
						setNewUserFormAssetMode(false);
						setOpenDeleteUserFormAsset(true);
						setUserFormAssetDataSelectedInTable(data);
					},
					icon: '<i class="fas fa-trash"></i>',
				});
			}

			const addNewUserFormAsset: (DefaultMenuItem | MenuItemDef)[] = [];

			if (!data && FormAssetIdToSearchUserFormAssets !== null) {
				addNewUserFormAsset.push({
					name: "Add User",
					action: () => {
						setUserFormAssetDataSelectedInTable(INITIAL_VALUES_USER_FORM_ASSET);
						setNewUserFormAssetMode(true);
						setOpenNewEditUserFormAsset(true);
					},
					icon: '<i class="fas fa-plus"></i>',
				});
			}

			return data ? result : addNewUserFormAsset;
		},
		[usersFormAssetList],
	);

	const renderColumns = () => {
		setUsersFormAssetColumnDefs([]);
		const newColumns: ColDef[] = [];
		newColumns.push({
			field: "UserName",
			headerName: "User",
			sortable: false,
			flex: 4,
			minWidth: 130,
		}),
			newColumns.push({
				field: "IsDefault",
				headerName: "Default",
				sortable: false,
				flex: 1,
				minWidth: 80,
				valueFormatter: (params) => (params.value ? "Yes" : "No"),
			});
		newColumns.push({
			headerName: "",
			flex: 1,
			minWidth: 65,
			maxWidth: 65,
			cellClass: "ag-columns",
			wrapText: false,
			autoHeight: false,
			cellRenderer: (params: EditableCallbackParams) => {
				return (
					<Grid2
						container
						style={{
							height: "100%",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<Grid2
							style={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
							size={{ xs: 12, md: 12 }}
						>
							<Grid2
								container
								spacing={1}
								style={{ justifyContent: "flex-end", minWidth: 30 }}
							>
								<Grid2 size={{ xs: 12, md: 12 }}>
									<Button
										fullWidth
										style={{}}
										onClick={(e) => onActionsClick(e, params)}
									>
										<FormatListBulletedSharpIcon
											style={{ height: "auto" }}
											color="action"
										/>
									</Button>
								</Grid2>
							</Grid2>
						</Grid2>
					</Grid2>
				);
			},
		});
		setUsersFormAssetColumnDefs(newColumns);
	};

	useEffect(() => {
		renderColumns();
	}, [usersFormAssetList]);

	useEffect(() => {
		if (isLoading) {
			gridApi?.showLoadingOverlay();
		} else gridApi?.hideOverlay();
	}, [isLoading, usersFormAssetList, gridApi]);

	useEffect(() => {
		if (FormAssetIdToSearchUserFormAssets) {
			searchUsersFormAsset();
			searchFormAsset();
		}
	}, [FormAssetIdToSearchUserFormAssets]);
	return (
		<>
			<Grid2
				container
				style={{ paddingLeft: 10, height: "59vh" }}
				size={{ md: 8, xs: 6 }}
			>
				{isLoading ? (
					<Grid2
						style={{
							height: "59vh",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
						size={{ md: 12, xs: 12 }}
					>
						<LazyLoading />
					</Grid2>
				) : (
					<>
						<Grid2
							style={{
								height: "58%",
							}}
							size={{ md: 12, xs: 12 }}
						>
							{FormAssetId && <EditFormAsset FormAsset={FormAsset} />}
						</Grid2>
						{FormAssetIdToSearchUserFormAssets && (
							<Grid2
								style={{
									height: "42%",
									border: "4px solid #ccc",
									borderRadius: 10,
								}}
								size={{ md: 12, xs: 12 }}
							>
								<div style={gridStyle}>
									<AgGridReact
										rowData={usersFormAssetList}
										columnDefs={UsersFormAssetColumnDefs}
										defaultColDef={globalDefaultColDef}
										rowHeight={38}
										headerHeight={42}
										loadingOverlayComponent={loadingOverlayComponent}
										animateRows={true}
										getContextMenuItems={(e: any) =>
											getUsersFormAssetContextMenuItems(e)
										}
										pagination={false}
										onRowDoubleClicked={(event) => {
											setNewUserFormAssetMode(false);
											setUserFormAssetDataSelectedInTable(event.data);
											setOpenNewEditUserFormAsset(true);
										}}
										rowSelection="single"
										onGridReady={(params) => setGridApi(params.api)}
										overlayNoRowsTemplate={"No users"}
									/>
								</div>
							</Grid2>
						)}
					</>
				)}
			</Grid2>
			<NewAndEditUserFormAssetModal
				show={openNewEditUserFormAsset}
				data={userFormAssetDataSelectedInTable}
				FormAssetIdToSearchUserFormAssets={
					FormAssetIdToSearchUserFormAssets as number
				}
				newMode={newUserFormAssetMode}
				setShouldUpdateWhenExit={setShouldUpdateWhenExit}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						searchUsersFormAsset();
					}
					setOpenNewEditUserFormAsset(false);
				}}
			/>
			<DeleteUserFormAssetDialog
				show={openDeleteUserFormAsset}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						searchUsersFormAsset();
						setShouldUpdateWhenExit(true);
					}
					setOpenDeleteUserFormAsset(false);
				}}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
